"use client"

import Media from "@/(admin)/modules/media/pages/Media"
import { Images } from "@/constants/images"
import { t } from "@/languages/i18n"
import { cn, toImageSrc } from "@/lib/utils"
import { DeleteOutlined, FilePdfOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Image, Modal } from "antd"
import React, { useState } from "react"
import { MediaData } from "../../../lib/config/types"

interface ImageModalProps {
  selectedMedia?: MediaData[]
  setSelectedMedia: (media: MediaData[]) => void
  selectMode: "single" | "multi"
  className?: string
  type?: 'image' | 'pdf'
}

const ImageModal: React.FC<ImageModalProps> = ({ selectedMedia, setSelectedMedia, selectMode, className, type = 'image' }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleSelectMedia = (media: MediaData | MediaData[]) => {
    if (Array.isArray(media)) {
      setSelectedMedia([...(selectedMedia || []), ...media])
    } else {
      setSelectedMedia([media])
    }
    setIsModalOpen(false)
  }

  const handleRemoveImage = (index: number) => {
    const updatedMedia = (selectedMedia || []).filter((_, i) => i !== index)
    setSelectedMedia(updatedMedia)
  }

  const isPdf = (media?: MediaData) => {
    const url = media?.attributes?.url || media?.url || ""
    return type === 'pdf' || url.toLowerCase().endsWith('.pdf')
  }

  const PdfPreviewItem = ({ fileUrl }: { fileUrl: string }) => (
    <div className="flex flex-col items-center justify-center p-2">
      <FilePdfOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />
      <span className="text-[10px] text-gray-500 mt-2 text-center line-clamp-2 break-all">
        {fileUrl.split('/').pop() || t("PDF File")}
      </span>
    </div>
  )

  const BoxAddImage = () => {
    return (
      <div
        className="w-[150px] h-[150px] border text-[#1677ff] border-blue-400 rounded-lg flex items-center justify-center cursor-pointer flex-col gap-2"
        onClick={handleOpenModal}
      >
        <PlusOutlined className="text-2xl" />
        <div className="text-sm">{t("Thêm ảnh")}</div>
      </div>
    )
  }
  return (
    <div
      className={cn("border border-gray-300 rounded-[10px] mb-5", selectMode === "single" ? "w-fit" : "", className)}
    >
      <div className="m-4">
        {!selectedMedia || selectedMedia.length === 0 ? (
          <div className={cn("flex gap-4", selectMode === "single" ? "flex-col" : "flex-row")}>
            {selectMode === "multi" ? (
              <BoxAddImage />
            ) : (
              <>
                {isPdf() ? (
                  <FilePdfOutlined
                    className="w-[150px] h-[150px] bg-gray-50 border border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer"
                    style={{ fontSize: '48px', color: '#d9d9d9' }}
                    onClick={handleOpenModal} />
                ) : (
                  <Image
                    src={Images.imgEmpty.src}
                    alt="featured"
                    className="w-full cursor-pointer"
                    preview={false}
                    onClick={handleOpenModal}
                    width={150}
                    height={150} />
                )}
                <Button variant="outlined" color="primary" icon={<PlusOutlined />} onClick={handleOpenModal}>
                  {isPdf() ? t("Select PDF") : t("Add Image")}
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className={cn("flex flex-row gap-4 flex-wrap", selectMode === "single" ? "flex-col" : "")}>
            {selectedMedia.map((media, index) => {
              const url = media?.attributes?.url || media?.url || ""
              const displayUrl = toImageSrc(url)

              return (
              <div
                key={index}
                className="relative w-[150px] h-[150px] border border-gray-200 rounded-lg hover:scale-105 transition-all duration-300 overflow-hidden"
              >
                  {isPdf() ? (
                    <PdfPreviewItem fileUrl={url} />
                  ) : (
                    <Image
                      src={displayUrl}
                      alt="featured"
                      className="shadow-lg rounded-lg cursor-pointer object-cover !h-[150px] !w-[150px]"
                    />)}
                <div
                  className="absolute top-1 right-1 h-8 w-8 flex items-center justify-center cursor-pointer bg-red-500 rounded-lg border-white border hover:scale-110 transition-all duration-300"
                  onClick={() => handleRemoveImage(index)}
                >
                  <DeleteOutlined className="text-white" />
                </div>
              </div>
              )
            })}

            {selectMode === "multi" ? (
              <BoxAddImage />
            ) : (
              <Button variant="outlined" color="primary" onClick={handleOpenModal}>
                {isPdf() ? t("Change PDF") : t("Change Image")}
              </Button>
            )}
          </div>
        )}
      </div>

      <Modal
        open={isModalOpen}
        onCancel={handleCloseModal}
        style={{ top: 20 }}
        width="95%"
        footer={null}
        styles={{ body: { padding: "20px" } }}
      >
        <div className="-ml-4">
          <Media
            isOpenModal={true}
            selectMode={selectMode}
            onSelectMedia={handleSelectMedia}
          />
        </div>
      </Modal>
    </div>
  )
}

export default ImageModal
